import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { IntegrationServices } from '@sequencehq/api/utils'
import {
  EntityTypeSyncToggle,
  findLiveCustomerPushToggle
} from 'Integrations/integrationsConfig/common/AutoLinkCustomers/entityTypeSyncToggle.helpers'

// The backend has no idempotent upsert for sync toggles, so creating one is a
// check-then-act race. A concurrent create (a double Save, or the Rillet hook
// saving alongside a manual save) makes our POST fail with one of these
// statuses; the row exists by then, so we refetch and update it instead.
// Remove this compensation once the API can upsert.
const DUPLICATE_CREATE_STATUSES = [400, 409]

/**
 * Reconciles the customer-push toggle for a service to `enabled`, list-first:
 * update the existing live toggle, create one when none exists, or do nothing
 * when it already matches. Returns true on success.
 *
 * Shared by the settings mutators (persist on "Save changes") and the Rillet
 * self-saving hook.
 */
export const persistAutoLinkCustomers = async (
  service: IntegrationServices,
  enabled: boolean
): Promise<boolean> => {
  const list = await dashboardv99990101Client.getEntityTypeSyncToggles()
  if (list.error) {
    return false
  }

  const existing = findLiveCustomerPushToggle(list.data?.items ?? [], service)
  if (existing) {
    return reconcileToggle(existing, enabled)
  }

  // Nothing to create when the user is switching auto-link off.
  if (!enabled) {
    return true
  }

  const created = await dashboardv99990101Client.postEntityTypeSyncToggle({
    body: {
      integrationService: service,
      entityType: 'CUSTOMER',
      syncDirection: 'PUSH',
      enabled
    }
  })
  if (!created.error) {
    return true
  }
  if (!DUPLICATE_CREATE_STATUSES.includes(created.res.status)) {
    return false
  }

  const refetched = await dashboardv99990101Client.getEntityTypeSyncToggles()
  const raced = findLiveCustomerPushToggle(refetched.data?.items ?? [], service)
  return raced ? reconcileToggle(raced, enabled) : false
}

const reconcileToggle = async (
  toggle: EntityTypeSyncToggle,
  enabled: boolean
): Promise<boolean> => {
  if (toggle.enabled === enabled) {
    return true
  }

  const updated = await dashboardv99990101Client.putEntityTypeSyncToggle({
    id: toggle.id,
    body: { enabled }
  })
  return !updated.error
}
